import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import posthog from "posthog-js";
import type { translations, Lang } from "../i18n";
import { trackInitiateCheckout, trackSchedule, trackFormSubmit } from "../lib/pixel";
import { buildRegisterUrl, getAttributionParams } from "../lib/tracking-url";
import { ctaSource, demoUrl, scrollToForm, type Cta, type VorsprungVariant } from "../lib/cta";
import { submitLeadForm } from "../lib/leadForm";
import ProofStrip from "../components/landing/ProofStrip";
import {
  DIGEST_RECORD,
  MONITOR_RECORDS,
  pickStage,
  pickReason,
} from "../data/vorsprung-records";

type T = (typeof translations)["de"]["vorsprung"];
type FormT = (typeof translations)["de"]["anfrage"];
type ProofT = (typeof translations)["de"]["proof"];

type Props = {
  navigate: (path: string) => void;
  t: T;
  proofT: ProofT;
  lang: Lang;
  pricingSlot?: React.ReactNode;
  variant?: VorsprungVariant;
  /** Required when `variant === "form"` — drives the inline lead-form section. */
  formT?: FormT;
  /** "form" only: "lean" = ad page with the form last; "classic" = full page with the form in the hero. */
  formLayout?: "lean" | "classic";
};

// Forwards utm_*/gclid/fbclid to the app so cross-domain signups keep attribution.
// Computed per render (not module scope) so params restored after the 404-redirect
// or captured later are always picked up.
const registerUrl = () => buildRegisterUrl("https://app.neosearch.neobim.ai/start");

const MARKO_LINKEDIN = "https://www.linkedin.com/in/mrarchitect/";

export default function VorsprungPage({
  navigate,
  t,
  proofT,
  lang,
  pricingSlot,
  variant = "signup",
  formT,
  formLayout = "lean",
}: Props) {
  const source = ctaSource(variant);

  const onGetDigest = () => {
    trackInitiateCheckout({ source });
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "cta_try_free", source });
    }
  };

  const onBookDemo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackSchedule({ source });
    navigate(demoUrl());
  };

  const onScrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "cta_scroll_to_form", source });
    }
    if (posthog.__loaded) posthog.capture("cta_scroll_to_form", { source });
    scrollToForm();
  };

  const cta: Cta =
    variant === "demo"
      ? { href: demoUrl(), onClick: onBookDemo }
      : variant === "form"
        ? { href: "#anfrage-form", onClick: onScrollToForm }
        : { href: registerUrl(), onClick: onGetDigest };

  // Ad landing: one path to the form — proof, product, steps, then the form.
  if (variant === "form" && formT && formLayout === "lean") {
    return (
      <div className="bg-white text-slate-900">
        <Hero t={t} lang={lang} cta={cta} variant={variant} />
        <ProofStrip t={proofT} />
        <Monitor t={t.monitor} lang={lang} cta={cta} />
        <Timeline t={t.timeline} cta={cta} />
        <FormSection t={formT} lang={lang} navigate={navigate} />
        <Faq t={t.faq} />
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-900">
      <Hero
        t={t}
        lang={lang}
        cta={cta}
        variant={variant}
        heroForm={variant === "form" && formT ? { t: formT, navigate } : undefined}
      />
      <ProofStrip t={proofT} />
      <Timeline t={t.timeline} cta={cta} />
      <BeforeAfter t={t.beforeAfter} />
      <Monitor t={t.monitor} lang={lang} cta={cta} />
      <Digest t={t.digest} cta={cta} />
      {/* "Für wen ist NeoSearch" — hidden for now; copy kept in t.audience. */}
      {pricingSlot}
      <Demo t={t.demo} onBookDemo={onBookDemo} />
      <Faq t={t.faq} />
      <CustomerQuote t={t.customerQuote} />
      <TrustStrip t={t} />
      <FooterCta t={t.footerCta} cta={cta} />
    </div>
  );
}

function Hero({
  t,
  lang,
  cta,
  variant,
  heroForm,
}: {
  t: T;
  lang: Lang;
  cta: Cta;
  variant?: VorsprungVariant;
  /** Classic /anfrage-v1: the lead form takes the hero's right column and is the CTA. */
  heroForm?: { t: FormT; navigate: (path: string) => void };
}) {
  // screen.height (not innerHeight) so mobile address-bar resizing doesn't flicker this.
  const [shortPhone, setShortPhone] = useState(
    () => typeof window !== "undefined" && window.screen.height < 740
  );
  useEffect(() => {
    const update = () => setShortPhone(window.screen.height < 740);
    window.addEventListener("orientationchange", update);
    return () => window.removeEventListener("orientationchange", update);
  }, []);

  // Lean /anfrage: ad visitors should reach the press + client logos without scrolling past a ticker.
  const lean = variant === "form" && !heroForm;
  const showMobileStack = !shortPhone && variant !== "form";

  return (
    <section
      className={`bg-white px-5 sm:px-6 ${
        lean
          ? "pt-28 pb-10 sm:pb-12 lg:pt-24 lg:pb-10"
          : "pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-32 lg:pb-24"
      }`}
    >
      <div className="max-w-[1140px] mx-auto grid gap-10 lg:gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:items-center">
        <div className="text-left max-w-[780px]">
          <h1 className="text-[30px] leading-[1.14] sm:text-[40px] sm:leading-[1.1] lg:text-[46px] lg:leading-[1.06] font-bold tracking-tight text-slate-900">
            {t.hero.h1}
          </h1>
          <p className="mt-5 sm:mt-6 text-[16px] sm:text-[18px] text-slate-600 leading-relaxed max-w-[620px]">
            {t.hero.sub}
          </p>
          {/* Same moving project stack as desktop, shorter. Dropped for a
              short phone so it never pushes the CTA below the fold. */}
          {showMobileStack && (
            <div className="lg:hidden mt-7 max-w-[440px]">
              <HeroProjectStack t={t} lang={lang} cta={cta} compact />
            </div>
          )}
          {!heroForm && (
            <div className="mt-7 sm:mt-8 flex flex-col items-start gap-2">
              <a
                href={cta.href}
                onClick={cta.onClick}
                className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-6 py-3.5 rounded-lg transition-all hover:-translate-y-px hover:shadow-lg hover:shadow-orange-500/25 w-full sm:w-auto"
              >
                {t.hero.ctaPrimary}
              </a>
              {!lean && (
                <span className="text-[12.5px] text-slate-500">{t.hero.ctaNote}</span>
              )}
            </div>
          )}
        </div>

        {heroForm ? (
          <div className="mt-8 lg:mt-0 max-w-[440px] lg:max-w-none w-full">
            <CompactAnfrageForm t={heroForm.t} lang={lang} navigate={heroForm.navigate} />
          </div>
        ) : (
          // Compact on lean /anfrage so the press + client logos below fit the first screen.
          <div className="hidden lg:block">
            <HeroProjectStack t={t} lang={lang} cta={cta} compact={lean} />
          </div>
        )}
      </div>
    </section>
  );
}

function FormSection({
  t,
  lang,
  navigate,
}: {
  t: FormT;
  lang: Lang;
  navigate: (path: string) => void;
}) {
  return (
    <section className="bg-slate-50 px-5 sm:px-6 py-16 sm:py-20 border-t border-slate-200">
      <div className="max-w-[480px] mx-auto">
        <h2 className="text-[24px] leading-[1.18] sm:text-3xl font-extrabold tracking-tight text-slate-900">
          {t.heading}
        </h2>
        <p className="mt-3 mb-7 text-[15px] text-slate-600 leading-relaxed">{t.sub}</p>
        <CompactAnfrageForm t={t} lang={lang} navigate={navigate} />
      </div>
    </section>
  );
}

// Hero visual: real council decisions scrolling past. The list is rendered
// twice so the loop has no seam; the second copy is hidden from assistive
// tech. `compact` is the shorter mobile height.
function HeroProjectStack({
  t,
  lang,
  cta,
  compact = false,
}: {
  t: T;
  lang: Lang;
  cta: Cta;
  compact?: boolean;
}) {
  const records = MONITOR_RECORDS;
  const [paused, setPaused] = useState(false);
  return (
    <div className="relative bg-white border border-slate-200 rounded-2xl shadow-[0_18px_46px_rgba(15,23,42,0.10)] overflow-hidden">
      <style>{`
        @keyframes hero-stack-scroll {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        .hero-stack-track { animation: hero-stack-scroll 46s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hero-stack-track { animation: none; }
        }
      `}</style>

      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-slate-100 bg-slate-50/70">
        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">
          {t.weekCards.eyebrow}
        </span>
      </div>

      <div
        className={`hero-stack overflow-hidden ${compact ? "h-[212px]" : "h-[416px]"}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent 0, #000 9%, #000 88%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0, #000 9%, #000 88%, transparent 100%)",
        }}
      >
        <div
          className="hero-stack-track"
          style={{ animationPlayState: paused ? "paused" : "running" }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} aria-hidden={copy === 1 ? true : undefined}>
              {records.map((r) => (
                <a
                  key={r.id}
                  href={cta.href}
                  onClick={cta.onClick}
                  tabIndex={copy === 1 ? -1 : undefined}
                  className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0">
                    <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-orange-700">
                      {r.sector}
                    </span>
                    <div className="mt-1 text-[14px] font-bold leading-snug text-slate-900">
                      {r.title}
                    </div>
                    <div className="mt-1 text-[12px] text-slate-500">
                      {[r.city, pickStage(r, lang), r.decisionDateLabel]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <span className="text-[14px] font-black text-orange-700 whitespace-nowrap">
                    {r.budget}
                  </span>
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>

      <p className="px-5 py-3 border-t border-slate-100 bg-slate-50/70 text-[11.5px] text-slate-500">
        {t.hero.stackNote}
      </p>
    </div>
  );
}

function CustomerQuote({ t }: { t: T["customerQuote"] }) {
  const quotes = t.quotes;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!pausedRef.current) setIdx((i) => (i + 1) % quotes.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [quotes.length]);

  const go = (d: number) => setIdx((i) => (i + d + quotes.length) % quotes.length);

  return (
    <section
      className="relative isolate overflow-hidden bg-white border-y border-slate-200 px-5 sm:px-6 py-20 sm:py-24 lg:py-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Masked so the dots dissolve instead of tiling to a hard edge. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(15,23,42,0.10) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(ellipse 80% 75% at 50% 40%, #000 30%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 75% at 50% 40%, #000 30%, transparent 100%)",
        }}
      />

      <div className="relative max-w-[1100px] mx-auto">
        {/* t.label is retained as the tablist's accessible name. */}
        <h2 className="text-[24px] leading-[1.18] sm:text-3xl lg:text-[34px] lg:leading-[1.15] font-extrabold tracking-tight text-slate-900">
          {t.headingA} <span className="text-orange-600">{t.headingAccent}</span>{" "}
          {t.headingB}
        </h2>
        <p className="mt-3.5 text-[15px] sm:text-base text-slate-600 leading-relaxed">
          {t.sub}
        </p>

        {/* Crossfade: every slide shares one grid cell, so the outgoing and
            incoming overlap instead of cutting to blank, and the cell holds the
            tallest quote — nothing below it shifts as they rotate. */}
        <div className="grid mt-12 sm:mt-14 lg:mt-16">
          {quotes.map((q, i) => (
            <div
              key={q}
              aria-hidden={i !== idx}
              className={`col-start-1 row-start-1 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-x-8 lg:gap-x-12 transition-[opacity,visibility] duration-700 ease-out motion-reduce:transition-none ${
                i === idx ? "opacity-100 visible" : "opacity-0 invisible"
              }`}
            >
              <span
                aria-hidden="true"
                className="hidden sm:block text-[54px] lg:text-[72px] leading-none font-bold text-slate-900/[0.08] tabular-nums select-none -mt-1"
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <blockquote className="max-w-[720px]">
                {/* A clear step below the 34px/800 h2 so the two do not tie. */}
                <p className="text-balance text-[19px] leading-[1.5] sm:text-[22px] sm:leading-[1.46] lg:text-[25px] lg:leading-[1.44] font-medium tracking-[-0.005em] text-slate-700">
                  {q}
                </p>
                <footer className="mt-7 sm:mt-8">
                  <span className="block text-sm font-bold text-slate-900">
                    {t.author}
                  </span>
                </footer>
              </blockquote>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-10 sm:mt-12">
          <div className="flex items-center gap-2" role="tablist" aria-label={t.label}>
            {quotes.map((q, i) => (
              <button
                key={q}
                type="button"
                role="tab"
                aria-selected={i === idx}
                aria-label={q}
                onClick={() => setIdx(i)}
                className="py-2.5 -my-1 flex items-center group"
              >
                <span
                  className={`h-[2px] rounded-full transition-all duration-500 ${
                    i === idx
                      ? "w-9 bg-orange-500"
                      : "w-5 bg-slate-300 group-hover:bg-slate-400"
                  }`}
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[
              { d: -1, label: t.prevLabel, path: "M15 19l-7-7 7-7" },
              { d: 1, label: t.nextLabel, path: "M9 5l7 7-7 7" },
            ].map(({ d, label, path }) => (
              <button
                key={label}
                type="button"
                onClick={() => go(d)}
                aria-label={label}
                className="p-2.5 text-slate-400 hover:text-slate-900 transition-colors"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d={path} />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BeforeAfter({ t }: { t: T["beforeAfter"] }) {
  return (
    <section className="bg-slate-900 border-t border-white/10 px-5 sm:px-6 py-20 sm:py-20 lg:py-24">
      <div className="max-w-[1100px] mx-auto">
        <h2 className="text-[24px] leading-[1.18] sm:text-3xl lg:text-[34px] lg:leading-[1.15] font-extrabold tracking-tight text-white max-w-[680px]">
          {t.heading}
        </h2>

        <div className="grid gap-4 sm:grid-cols-2 mt-8 sm:mt-10 items-start">
          {/* Tender portals — the later, weaker option */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
            <div className="text-[13px] font-bold text-slate-300">{t.portalLabel}</div>
            <div className="h-px bg-white/10 my-4" />
            <div className="flex flex-col gap-2.5">
              {t.rows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2.5 text-[13.5px] sm:text-[14px] text-slate-400 leading-relaxed"
                >
                  <span className="text-slate-600 font-bold shrink-0">—</span>
                  <span>{row.without}</span>
                </div>
              ))}
            </div>
          </div>

          {/* NeoSearch — earlier, highlighted */}
          <div className="relative overflow-hidden rounded-2xl border border-orange-500/40 bg-orange-500/[0.06] p-5 sm:p-6">
            <div className="absolute top-0 right-0 bg-orange-500 text-white text-[11px] font-bold px-3.5 py-1.5 rounded-bl-xl">
              {t.badge}
            </div>
            <div className="text-[13px] font-bold text-orange-400 pr-24">{t.neoLabel}</div>
            <div className="h-px bg-white/10 my-4" />
            <div className="flex flex-col gap-2.5">
              {t.rows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2.5 text-[13.5px] sm:text-[14px] text-slate-100 leading-relaxed"
                >
                  <span className="text-orange-500 font-bold shrink-0">✓</span>
                  <span>{row.with}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip({ t }: { t: T }) {
  return (
    <section className="bg-slate-50 px-5 sm:px-6 py-8 sm:py-10 border-t border-slate-200">
      <div className="max-w-[1100px] mx-auto flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-center text-[12px] sm:text-[13px] text-slate-500">
        {t.coverage.stats.slice(0, 2).map((s) => (
          <span key={s.label} className="whitespace-nowrap">
            <span className="font-bold text-slate-900">{s.num}</span> {s.label}
          </span>
        ))}
        <span className="leading-snug">{t.scanner.dsgvo}</span>
      </div>
    </section>
  );
}

// Light on purpose: the tender-portal comparison directly below is the dark
// section, and two dark slabs back to back read as one and flatten the page.
function Timeline({ t, cta }: { t: T["timeline"]; cta: Cta }) {
  return (
    <section id="wie" className="bg-white px-5 sm:px-6 py-20 sm:py-20 lg:py-20">
      <div className="max-w-[1100px] mx-auto">
        <div className="max-w-2xl mb-10 sm:mb-12">
          <h2 className="text-[26px] leading-[1.15] sm:text-3xl lg:text-[36px] lg:leading-[1.12] font-black tracking-tight text-slate-900">
            {t.headLead}{" "}
            <span className="text-orange-600">{t.headAccent}</span>
          </h2>
          {t.lead && (
            <p className="mt-4 text-[15px] sm:text-base text-slate-600 leading-relaxed">
              {t.lead}
            </p>
          )}
        </div>

        <div className="grid gap-8 sm:gap-6 md:grid-cols-3 md:gap-8">
          {t.steps.map((step, idx) => (
            <div key={step.label} className="flex md:flex-col items-start gap-4 md:gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-sm font-extrabold text-white shrink-0">
                {idx + 1}
              </div>
              <div>
                <div className="text-base sm:text-[17px] font-bold text-slate-900 leading-snug mb-1.5">
                  {step.label}
                </div>
                {step.sub && (
                  <div className="text-[13px] sm:text-sm text-slate-600 leading-relaxed">
                    {step.sub}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <a
          href={cta.href}
          onClick={cta.onClick}
          className="inline-flex items-center justify-center mt-10 sm:mt-12 bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-6 py-3.5 rounded-lg transition-all hover:-translate-y-px hover:shadow-lg hover:shadow-orange-500/25 w-full sm:w-auto"
        >
          {t.cta}
        </a>
      </div>
    </section>
  );
}

function Digest({ t, cta }: { t: T["digest"]; cta: Cta }) {
  return (
    <section id="digest" className="bg-white px-5 sm:px-6 py-20 sm:py-20 lg:py-24 border-b border-slate-200">
      {/* Mobile order: heading/body → card → CTA, so the CTA is the last thing
          to reach. Desktop keeps text+CTA left, card right via explicit grid
          placement. */}
      <div className="max-w-[1100px] mx-auto grid gap-8 sm:gap-10 lg:gap-x-20 lg:gap-y-7 lg:grid-cols-2 lg:items-start">
        <div className="order-1 lg:col-start-1 lg:row-start-1">
          <h2 className="text-[24px] leading-[1.18] sm:text-3xl lg:text-[34px] lg:leading-[1.15] font-extrabold tracking-tight text-slate-900 mb-3.5">
            {t.heading}
          </h2>
          <p className="text-[15px] sm:text-base text-slate-600 leading-relaxed">
            {t.body}
          </p>
        </div>
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2">
          <a
            href={cta.href}
            onClick={cta.onClick}
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-5 py-3 rounded-lg transition-all hover:-translate-y-px w-full sm:w-auto justify-center sm:justify-start"
          >
            {t.cta}
          </a>
        </div>
        <div className="order-2 lg:order-none min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <div className="flex items-center gap-2.5 mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
            <span>{t.cardWeek}</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="bg-white border border-slate-200 rounded-[10px] p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                {DIGEST_RECORD.sector}
              </span>
              <span className="text-xs font-bold text-slate-900">
                {DIGEST_RECORD.budget}
              </span>
            </div>
            <div className="text-sm font-bold text-slate-900 leading-snug">
              {DIGEST_RECORD.title}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {[
                DIGEST_RECORD.city,
                DIGEST_RECORD.district,
                DIGEST_RECORD.decisionDateLabel,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mt-2">
              {t.signal.desc}
            </p>
            {/* Source shown as evidence text; the action goes to the app,
                never to the third-party portal. */}
            <a
              href={cta.href}
              onClick={cta.onClick}
              className="block mt-2.5 pt-2 border-t border-slate-100 text-[11px] text-slate-500 hover:text-orange-600"
            >
              <span className="text-slate-400">{DIGEST_RECORD.sourceLabel}</span>
              <span className="block font-semibold text-orange-700 hover:text-orange-800 mt-0.5">
                {t.sourceLabel}
              </span>
            </a>
          </div>
          <a
            href={cta.href}
            onClick={cta.onClick}
            className="inline-flex items-center text-xs font-semibold mt-3 text-orange-700 hover:text-orange-800 hover:underline underline-offset-2"
          >
            {t.hint}
          </a>
        </div>
      </div>
    </section>
  );
}

function Monitor({
  t,
  lang,
  cta,
}: {
  t: T["monitor"];
  lang: Lang;
  cta: Cta;
}) {
  const records = MONITOR_RECORDS;
  const N = records.length;
  const ROW_H = 56;
  const VISIBLE = 4;
  const WINDOW_H = VISIBLE * ROW_H;
  const STEP_S = 2.6; // seconds per row of scroll AND per highlight beat
  const SCROLL_DURATION = N * STEP_S;

  // The highlight is anchored to a specific record. To keep the highlight
  // jump perfectly in sync with the CSS scroll animation (separate clocks
  // would drift), we read the animation's elapsed currentTime each rAF and
  // derive highlightIdx from it directly.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [highlightIdx, setHighlightIdx] = useState(1);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const tick = () => {
      const el = trackRef.current;
      const anim = el?.getAnimations?.()[0];
      if (anim && typeof anim.currentTime === "number") {
        const elapsedS = anim.currentTime / 1000;
        // At t=0, row at slot 1 is doubled[1] → index 1. Each STEP_S seconds,
        // the track has scrolled up one ROW_H, so the highlight advances one.
        const idx = (Math.floor(elapsedS / STEP_S) + 1) % N;
        setHighlightIdx((prev) => (prev === idx ? prev : idx));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [N, STEP_S]);

  const doubled = [...records, ...records];

  return (
    <section
      id="monitor"
      className="bg-slate-50 px-5 sm:px-6 py-20 sm:py-20 lg:py-24 border-b border-slate-200"
    >
      <style>{`
        @keyframes adsv3-ticker {
          from { transform: translateY(0); }
          to   { transform: translateY(-${N * ROW_H}px); }
        }
        .adsv3-ticker-track {
          animation: adsv3-ticker ${SCROLL_DURATION}s linear infinite;
          will-change: transform;
        }
        .adsv3-ticker-window:hover .adsv3-ticker-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .adsv3-ticker-track { animation: none; }
        }
      `}</style>
      {/* Mobile order: heading/body → live ticker → CTA, so the CTA is the
          last thing to reach. Desktop keeps text+CTA left, ticker right. */}
      <div className="max-w-[1100px] mx-auto grid gap-8 sm:gap-10 lg:gap-x-16 lg:gap-y-7 lg:grid-cols-2 lg:items-start">
        <div className="order-1 lg:col-start-1 lg:row-start-1">
          <h2 className="text-[24px] leading-[1.18] sm:text-3xl lg:text-[34px] lg:leading-[1.15] font-extrabold tracking-tight text-slate-900 mb-3.5">
            {t.headingA}
            <br />
            {t.headingB}
          </h2>
          <p className="text-[15px] sm:text-base text-slate-600 leading-relaxed">
            {t.body}
          </p>
        </div>
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2">
          <a
            href={cta.href}
            onClick={cta.onClick}
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-5 py-3 rounded-lg transition-all hover:-translate-y-px w-full sm:w-auto justify-center sm:justify-start"
          >
            {t.cta}
          </a>
        </div>
        <div className="order-2 lg:order-none min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-[0_18px_50px_rgba(15,23,42,0.10)]">
          <div className="flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0 animate-pulse" />
            <span>{t.liveLabel}</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <div
            className="adsv3-ticker-window relative overflow-hidden mt-2.5"
            style={{ height: WINDOW_H }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-7 bg-gradient-to-b from-white to-transparent z-10" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-7 bg-gradient-to-t from-white to-transparent z-10" />
            <div ref={trackRef} className="adsv3-ticker-track">
              {doubled.map((row, i) => {
                const active = i % N === highlightIdx;
                const meta = [
                  pickStage(row, lang),
                  row.sector,
                  `${row.city}${row.district ? `-${row.district}` : ""}`,
                  row.decisionDateLabel,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <a
                    key={`${row.id}-${i}`}
                    href={cta.href}
                    onClick={cta.onClick}
                    tabIndex={i >= N ? -1 : undefined}
                    className={`flex items-center justify-between pl-3.5 pr-1 border-b border-slate-100 gap-3 relative ${
                      active
                        ? "bg-orange-500/[0.08] border-l-2 border-l-orange-500"
                        : "border-l-2 border-l-transparent hover:bg-white/40"
                    }`}
                    style={{ height: ROW_H }}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        active ? "bg-orange-500" : "bg-slate-300"
                      }`}
                    />
                    <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                      <span
                        className={`text-xs truncate ${
                          active
                            ? "text-slate-900 font-semibold"
                            : "text-slate-800 font-medium"
                        }`}
                      >
                        {row.title}
                      </span>
                      <span className="text-[10px] text-slate-600 truncate">
                        {meta}
                      </span>
                    </div>
                    <span
                      className={`text-[11px] font-bold whitespace-nowrap ${
                        active ? "text-orange-500" : "text-slate-600"
                      }`}
                    >
                      {row.budget}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
          <div className="mt-3 min-h-[58px] rounded-lg border border-orange-500/20 bg-orange-500/5 px-3.5 py-2.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-orange-700">
              {t.whyLabel}
            </span>
            <p className="text-xs text-slate-700 leading-snug mt-0.5">
              {pickReason(records[highlightIdx], lang)}
            </p>
          </div>
          <p className="text-[10px] text-slate-500 italic mt-2.5 text-right">
            {t.note}
          </p>
        </div>
      </div>
    </section>
  );
}

// Emits the visible FAQ as FAQPage structured data, reusing the same i18n
// items so the two never drift. Injected at runtime into <head>; the build
// prerenderer captures it after mount, so JS-blind crawlers and AI answer
// engines see it too.
function FaqJsonLd({ items }: { items: T["faq"]["items"] }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-faq-jsonld", "");
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [items]);
  return null;
}

function Faq({ t }: { t: T["faq"] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section id="faq" className="bg-white px-5 sm:px-6 py-20 sm:py-20 lg:py-24 border-t border-slate-200">
      <FaqJsonLd items={t.items} />
      <div className="max-w-[720px] mx-auto">
        <h2 className="text-[24px] sm:text-2xl lg:text-[32px] font-extrabold tracking-tight text-slate-900 mb-8 sm:mb-10 lg:mb-12">
          {t.heading}
        </h2>
        {t.items.map((item, idx) => {
          const isOpen = openIdx === idx;
          return (
            <div key={item.q} className="border-b border-slate-200 py-5">
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : idx)}
                className="w-full flex justify-between items-center gap-4 text-left text-[15px] font-semibold text-slate-900"
              >
                <span>{item.q}</span>
                <span
                  className={`text-lg text-slate-500 transition-transform shrink-0 ${
                    isOpen ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="mt-3">
                  <p className="text-[15px] text-slate-700 leading-relaxed">
                    {item.a}
                  </p>
                  {item.link && (
                    <a
                      href={item.link.href}
                      className="inline-flex items-center mt-2.5 text-sm font-semibold text-orange-700 hover:text-orange-800 hover:underline underline-offset-2"
                    >
                      {item.link.label ?? t.readMore}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Demo({
  t,
  onBookDemo,
}: {
  t: T["demo"];
  // Always books a real call, regardless of page variant — this section is
  // the "talk to a human" alternative even on the /anfrage form page.
  onBookDemo: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <section id="demo" className="bg-white px-5 sm:px-6 py-20 sm:py-20 lg:py-24 border-t border-slate-200">
      <div className="max-w-[1100px] mx-auto">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto">
          <h2 className="text-[24px] leading-[1.18] sm:text-3xl lg:text-[34px] lg:leading-[1.15] font-extrabold tracking-tight text-slate-900 mb-3.5">
            {t.heading}
          </h2>
          <p className="text-[15px] sm:text-base text-slate-600 leading-relaxed">
            {t.body}
          </p>
          <div className="flex items-center gap-3 mt-6">
            <img
              src="/marko.jpg"
              alt="Marko, Principal Architect at NeoSearch"
              width={48}
              height={48}
              loading="lazy"
              className="w-12 h-12 rounded-full object-cover shrink-0 ring-2 ring-orange-100"
            />
            <div>
              <a
                href={MARKO_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.signatureLinkLabel}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900 hover:text-orange-700 transition-colors"
              >
                {t.signatureName}
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="w-3.5 h-3.5 fill-current text-[#0A66C2]"
                >
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                </svg>
              </a>
              <div className="text-xs text-slate-500 mt-0.5">
                {t.signatureRole}
              </div>
            </div>
          </div>
          <a
            href={demoUrl()}
            onClick={onBookDemo}
            className="inline-flex items-center justify-center mt-6 sm:mt-7 bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-5 py-3.5 rounded-lg transition-all hover:-translate-y-px w-full sm:w-auto"
          >
            {t.cta}
          </a>
        </div>
      </div>
    </section>
  );
}

const anfrageInputBase =
  "w-full rounded-lg bg-white border px-3 py-2.5 text-[14px] text-slate-900 " +
  "placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-colors";
const anfrageInputValid =
  "border-slate-300 focus:border-orange-500 focus:ring-orange-500/15";
const anfrageInputInvalid = "border-red-400 focus:border-red-500 focus:ring-red-500/15";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Custom listbox instead of a native <select> — a plain browser select
// renders with the OS's own font/height/arrow, which stood out against the
// custom-styled text inputs around it.
function RegionSelect({
  value,
  onChange,
  onClose,
  placeholder,
  wholeGermany,
  states,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Fired when the listbox closes, for the parent's touched/blur tracking. */
  onClose: () => void;
  placeholder: string;
  wholeGermany: string;
  states: string[];
  invalid: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        onClose();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onClose]);

  const options = [wholeGermany, ...states];
  const select = (v: string) => {
    onChange(v);
    setOpen(false);
    onClose();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid}
        className={`${anfrageInputBase} ${
          invalid ? anfrageInputInvalid : anfrageInputValid
        } flex items-center justify-between gap-2 text-left`}
      >
        <span className={value ? "text-slate-900" : "text-slate-400"}>
          {value || placeholder}
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={`text-slate-400 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {/* Always mounted (not `{open && ...}`) so it gets an animated
          entrance AND exit instead of an instant show/hide. */}
      <div
        role="listbox"
        aria-hidden={!open}
        className={`absolute z-20 mt-1.5 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1 origin-top transition-[opacity,transform] duration-150 ease-out ${
          open
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
        }`}
      >
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            role="option"
            tabIndex={open ? 0 : -1}
            aria-selected={value === opt}
            onClick={() => select(opt)}
            className={`w-full text-left px-3 py-2 text-[14px] transition-colors ${
              value === opt
                ? "bg-orange-50 text-orange-700 font-semibold"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

declare global {
  interface Window {
    lintrk?: (action: "track", params: { conversion_id: number }) => void;
  }
}

const LINKEDIN_ANFRAGE_CONVERSION_ID = 30524953;

type AnfrageStatus = "idle" | "submitting" | "success" | "error";
type AnfrageField = "name" | "email" | "company" | "intent" | "region";

// Compact lead-capture card: last section of lean /anfrage, hero column of /anfrage-v1.
// Deliberately terse (single region select, no optional message field) so it
// fits in view without scrolling inside the card.
function CompactAnfrageForm({
  t,
  lang,
  navigate,
}: {
  t: FormT;
  lang: Lang;
  navigate: (path: string) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [intent, setIntent] = useState("");
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState<AnfrageStatus>("idle");
  const [touched, setTouched] = useState<Partial<Record<AnfrageField, boolean>>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const values: Record<AnfrageField, string> = { name, email, company, intent, region };

  // null = valid. Checked per field so the message can be specific
  // ("required" vs. "not a valid email") instead of one generic blocker.
  const fieldError = (field: AnfrageField): string | null => {
    const v = values[field].trim();
    if (!v) return t.requiredError;
    if (field === "email" && !EMAIL_RE.test(v)) return t.emailInvalidError;
    return null;
  };

  const showError = (field: AnfrageField) =>
    (touched[field] || attemptedSubmit) && fieldError(field);

  const markTouched = (field: AnfrageField) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    const hasError = (Object.keys(values) as AnfrageField[]).some(
      (field) => fieldError(field) !== null
    );
    if (hasError || status === "submitting") return;

    setStatus("submitting");

    try {
      await submitLeadForm({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        intent: intent.trim(),
        regions: [region],
        message: "",
        lang,
      });

      setStatus("success");

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "lead_form_submitted",
          event_category: "conversion",
          event_label: "anfrage",
          ...getAttributionParams(),
        });
      }
      if (posthog.__loaded) {
        posthog.capture("lead_form_submitted", { source: "anfrage", lang });
      }
      trackFormSubmit({ source: "anfrage" });
      window.lintrk?.("track", { conversion_id: LINKEDIN_ANFRAGE_CONVERSION_ID });
    } catch {
      setStatus("error");
    }
  };

  const inputClass = (field: AnfrageField) =>
    `${anfrageInputBase} ${showError(field) ? anfrageInputInvalid : anfrageInputValid}`;

  // Same attributed booking link as every other "Demo buchen" CTA on this
  // page (see Demo()) — utm_*/gclid carried through via demoUrl().
  const onBookDemo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackSchedule({ source: "anfrage_success" });
    navigate(demoUrl());
  };

  return (
    <div
      id="anfrage-form"
      className="scroll-mt-24 bg-white border border-slate-200 rounded-2xl shadow-[0_18px_46px_rgba(15,23,42,0.10)]"
    >
      {/* Rounded explicitly (not via a parent `overflow-hidden`) so the
          region dropdown's panel below can overflow the card instead of
          being clipped by it. */}
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-slate-100 bg-slate-50/70 rounded-t-2xl">
        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">
          {t.eyebrow}
        </span>
      </div>

      {/* min-h keeps the card the same footprint before/after submit — the
          success message alone is much shorter than the full form, and
          without this the card visibly shrinks/jumps on submit. */}
      <div className="p-5 min-h-[420px] flex flex-col">
        {status === "success" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
            <h2 className="text-[17px] font-bold text-slate-900">{t.successTitle}</h2>
            <p className="mt-2 text-[13px] text-slate-600 leading-relaxed max-w-[280px]">
              {t.successBody}
            </p>
            <p className="mt-5 text-[13px] text-slate-600">
              {t.successDemoPrompt}
              <br />
              <a
                href={demoUrl()}
                onClick={onBookDemo}
                className="mt-1.5 inline-block font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2"
              >
                {t.successDemoCta} →
              </a>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-2.5">
              <label className="block">
                <span className="block text-[12px] font-semibold text-slate-900 mb-1">
                  {t.nameLabel} *
                </span>
                <input
                  className={inputClass("name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={markTouched("name")}
                  autoComplete="name"
                  aria-invalid={!!showError("name")}
                />
                {showError("name") && (
                  <span className="block mt-1 text-[11.5px] font-medium text-red-600">
                    {fieldError("name")}
                  </span>
                )}
              </label>
              <label className="block">
                <span className="block text-[12px] font-semibold text-slate-900 mb-1">
                  {t.emailLabel} *
                </span>
                <input
                  type="email"
                  className={inputClass("email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={markTouched("email")}
                  autoComplete="email"
                  aria-invalid={!!showError("email")}
                />
                {showError("email") && (
                  <span className="block mt-1 text-[11.5px] font-medium text-red-600">
                    {fieldError("email")}
                  </span>
                )}
              </label>
            </div>

            <label className="block">
              <span className="block text-[12px] font-semibold text-slate-900 mb-1">
                {t.companyLabel} *
              </span>
              <input
                className={inputClass("company")}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onBlur={markTouched("company")}
                autoComplete="organization"
                aria-invalid={!!showError("company")}
              />
              {showError("company") && (
                <span className="block mt-1 text-[11.5px] font-medium text-red-600">
                  {fieldError("company")}
                </span>
              )}
            </label>

            <label className="block">
              <span className="block text-[12px] font-semibold text-slate-900 mb-1">
                {t.intentLabel} *
              </span>
              <input
                className={inputClass("intent")}
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                onBlur={markTouched("intent")}
                placeholder={t.intentPlaceholder}
                aria-invalid={!!showError("intent")}
              />
              {showError("intent") && (
                <span className="block mt-1 text-[11.5px] font-medium text-red-600">
                  {fieldError("intent")}
                </span>
              )}
            </label>

            <label className="block">
              <span className="block text-[12px] font-semibold text-slate-900 mb-1">
                {t.regionsLabel} *
              </span>
              <RegionSelect
                value={region}
                onChange={setRegion}
                onClose={markTouched("region")}
                placeholder={t.regionPlaceholder}
                wholeGermany={t.wholeGermany}
                states={t.states}
                invalid={!!showError("region")}
              />
              {showError("region") && (
                <span className="block mt-1 text-[11.5px] font-medium text-red-600">
                  {fieldError("region")}
                </span>
              )}
            </label>

            {status === "error" && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2.5">
                <p className="font-semibold text-red-700 text-[12.5px]">{t.errorTitle}</p>
                <p className="mt-0.5 text-[11.5px] text-red-600/90">{t.errorBody}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full inline-flex items-center justify-center bg-orange-500 enabled:hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-[14px] font-semibold px-5 py-3 rounded-lg transition-all"
            >
              {status === "submitting"
                ? t.submitting
                : status === "error"
                  ? t.retryCta
                  : t.submitCta}
            </button>
            <p className="text-[11.5px] text-slate-500 text-center">{t.requiredNote}</p>
          </form>
        )}
      </div>
    </div>
  );
}

function FooterCta({ t, cta }: { t: T["footerCta"]; cta: Cta }) {
  return (
    <section className="bg-slate-900 px-5 sm:px-6 py-20 sm:py-20 lg:py-20 text-center">
      <h2 className="text-[26px] leading-[1.15] sm:text-3xl lg:text-[40px] lg:leading-tight font-black tracking-tight text-white max-w-2xl mx-auto">
        {t.heading}
      </h2>
      <div className="flex flex-col items-center gap-2 mt-6 sm:mt-7">
        <a
          href={cta.href}
          onClick={cta.onClick}
          className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white text-[15px] font-semibold px-6 py-3.5 rounded-lg transition-all hover:-translate-y-px hover:shadow-lg hover:shadow-orange-500/40 w-full sm:w-auto max-w-xs"
        >
          {t.cta}
        </a>
        <span className="text-[12.5px] text-slate-400">{t.ctaNote}</span>
      </div>
    </section>
  );
}
